import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { setPageSeo, resetPageSeo, SEO_BASE } from "@/lib/seo";
import { Loader2, ArrowLeft, Flame, Facebook, Instagram, Linkedin, Youtube, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SOCIAL_PLATFORMS, hasSocials } from "@/lib/socials";
import { useTranslation } from "react-i18next";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const XIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>);
const PinterestIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.225 7.464-1.216 0-2.357-.631-2.748-1.378l-.747 2.848c-.271 1.043-1.002 2.35-1.492 3.146 1.124.347 2.317.535 3.554.535 6.62 0 11.99-5.367 11.99-11.987C24.007 5.367 18.637.001 12.017.001z" /></svg>);
const TikTokIcon = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" /></svg>);

const NETWORKS = {
  facebook: { label: "Facebook", Icon: Facebook, hover: "#1877F2" },
  instagram: { label: "Instagram", Icon: Instagram, hover: "#E4405F" },
  twitter: { label: "X", Icon: XIcon, hover: "#ffffff" },
  linkedin: { label: "LinkedIn", Icon: Linkedin, hover: "#0A66C2" },
  tiktok: { label: "TikTok", Icon: TikTokIcon, hover: "#ffffff" },
  youtube: { label: "YouTube", Icon: Youtube, hover: "#FF0000" },
  pinterest: { label: "Pinterest", Icon: PinterestIcon, hover: "#BD081C" },
};
const NET_ORDER = ["facebook", "instagram", "twitter", "linkedin", "tiktok", "youtube", "pinterest"];

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeNets, setActiveNets] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/blog/${slug}?lang=${lang}`);
      setPost(r.data);
    } catch { setNotFound(true); }
    finally { setLoading(false); }
  }, [slug, lang]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/social-networks/active").then((r) => setActiveNets(r.data?.networks || [])).catch(() => setActiveNets([]));
  }, []);

  const shareArticle = (network) => {
    const raw = `${window.location.origin}/blog/${post.slug}`;
    const url = encodeURIComponent(raw);
    const msg = post.title;
    const text = encodeURIComponent(msg);
    const openShare = (u) => window.open(u, "_blank", "noopener,noreferrer");
    const nativeOrCopy = () => {
      const data = { title: post.title, text: `${msg}\n${raw}`, url: raw };
      if (navigator.share) { navigator.share(data).catch(() => {}); }
      else { navigator.clipboard.writeText(`${msg}\n${raw}`); toast.success(t("blog.linkCopied", "Lien copié")); }
    };
    switch (network) {
      case "facebook": return openShare(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
      case "twitter": return openShare(`https://twitter.com/intent/tweet?text=${text}&url=${url}`);
      case "linkedin": return openShare(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`);
      case "pinterest": return openShare(`https://pinterest.com/pin/create/button/?url=${url}&description=${text}`);
      case "instagram":
      case "tiktok":
      case "youtube": return nativeOrCopy();
      case "copy":
        navigator.clipboard.writeText(raw);
        toast.success(t("blog.linkCopied", "Lien copié"));
        return;
      default: return;
    }
  };

  useEffect(() => {
    if (!post) return;
    const img = post.cover_image_url ? `${SEO_BASE}${post.cover_image_url}` : `${SEO_BASE}/og-image.png`;
    const url = `${SEO_BASE}/blog/${post.slug}`;
    setPageSeo({
      title: `${post.title} | BougieDuCœur`,
      description: post.excerpt || post.title,
      canonicalPath: `/blog/${post.slug}`,
      image: img,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "description": post.excerpt,
        "image": img,
        "inLanguage": post.lang || lang,
        "datePublished": post.published_at,
        "dateModified": post.updated_at || post.published_at,
        "mainEntityOfPage": url,
        "author": { "@type": "Organization", "name": "BougieDuCœur", "url": `${SEO_BASE}/` },
        "publisher": { "@type": "Organization", "name": "BougieDuCœur", "logo": { "@type": "ImageObject", "url": `${SEO_BASE}/logo.png` } },
      },
    });
    document.querySelectorAll('link[data-bdc-hreflang]').forEach((n) => n.remove());
    (post.available_langs || []).forEach((l) => {
      const link = document.createElement("link");
      link.rel = "alternate"; link.hreflang = l; link.href = `${url}?lang=${l}`;
      link.setAttribute("data-bdc-hreflang", "1");
      document.head.appendChild(link);
    });
    return () => {
      resetPageSeo();
      document.querySelectorAll('link[data-bdc-hreflang]').forEach((n) => n.remove());
    };
  }, [post, lang]);

  if (loading) return <div className="flex min-h-[100dvh] items-center justify-center bg-black"><Loader2 className="animate-spin text-sky-400" /></div>;
  if (notFound || !post) return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-black text-white">
      <p className="text-white/60">{t("blog.notFound")}</p>
      <Button onClick={() => navigate("/blog")} variant="outline" className="rounded-full border-white/15">{t("blog.backToBlog")}</Button>
    </div>
  );

  const shareNets = NET_ORDER.filter((k) => activeNets.includes(k));

  return (
    <div className="min-h-[100dvh] bg-black text-white">
      <header className="border-b border-white/10 px-5 py-5">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <button onClick={() => navigate("/blog")} className="flex items-center gap-1.5 text-sm text-white/60 hover:text-white" data-testid="blog-back-link"><ArrowLeft size={16} /> {t("blog.back")}</button>
          <img src={`${BACKEND}/logo.png`} alt="BougieDuCœur" className="h-8 w-auto cursor-pointer" onClick={() => navigate("/")} />
        </div>
      </header>
      <article className="mx-auto max-w-3xl px-5 py-10 pb-24" data-testid="blog-article">
        {post.cover_image_url && (
          post.candle_id ? (
            <button type="button" onClick={() => navigate(`/candle/${post.candle_id}`)} className="group mb-8 block w-full cursor-pointer overflow-hidden rounded-2xl border border-white/10 transition-transform hover:scale-[1.01]" data-testid="blog-cover-link" title="Voir la bougie">
              <img src={`${BACKEND}${post.cover_image_url}`} alt={post.title} className="w-full object-cover" />
            </button>
          ) : (
            <img src={`${BACKEND}${post.cover_image_url}`} alt={post.title} className="mb-8 w-full rounded-2xl border border-white/10 object-cover" />
          )
        )}
        <h1 className="text-3xl font-medium leading-tight tracking-tight sm:text-4xl" data-testid="blog-title">{post.title}</h1>
        {post.candle_id && (
          <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-amber-300/30 bg-amber-300/[0.05] p-6 text-center">
            <p className="text-sm text-white/70">{t("blog.ctaText")}</p>
            <Button onClick={() => navigate(`/candle/${post.candle_id}`)} className="rounded-full bg-amber-300 text-black hover:bg-amber-200" data-testid="blog-candle-cta"><Flame size={16} className="mr-2" /> {t("blog.viewFlame")}</Button>
          </div>
        )}
        {!post.candle_id && (
          <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-amber-300/30 bg-amber-300/[0.05] p-6 text-center" data-testid="blog-topic-cta">
            <p className="text-base font-medium text-amber-100">{t("tss.offerTitle")}</p>
            <p className="text-sm text-white/70">{t("tss.ctaText")}</p>
            <Button onClick={() => navigate("/toussaint")} className="rounded-full bg-amber-300 text-black hover:bg-amber-200" data-testid="blog-topic-cta-btn"><Flame size={16} className="mr-2" /> {t("tss.ctaBtn")}</Button>
          </div>
        )}

        {hasSocials(post.deceased_socials) && (
          <div className="mt-8" data-testid="blog-deceased-socials">
            <p className="mb-3 text-center text-sm text-white/60">Réseaux sociaux{post.deceased_name ? ` de ${post.deceased_name}` : ""}</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {SOCIAL_PLATFORMS.filter((p) => post.deceased_socials[p.key]).map((p) => (
                <a
                  key={p.key}
                  href={post.deceased_socials[p.key]}
                  target="_blank"
                  rel="noreferrer"
                  title={p.label}
                  aria-label={p.label}
                  style={{ "--sh": p.color }}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/60 transition-colors hover:border-[var(--sh)] hover:text-[var(--sh)]"
                  data-testid={`blog-deceased-social-${p.key}`}
                >
                  <p.Icon size={18} />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8" data-testid="blog-social-share">
          <p className="mb-3 text-center text-sm text-white/60">{t("blog.shareArticle", "Partager cet article")}</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {shareNets.map((key) => {
              const { label, Icon, hover } = NETWORKS[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => shareArticle(key)}
                  aria-label={`Partager sur ${label}`}
                  style={{ "--sh": hover }}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/60 transition-colors hover:border-[var(--sh)] hover:text-[var(--sh)]"
                  data-testid={`blog-share-${key}`}
                >
                  <Icon size={18} />
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => shareArticle("copy")}
              aria-label="Copier le lien"
              title="Copier le lien"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/60 transition-colors hover:border-amber-300 hover:text-amber-300"
              data-testid="blog-share-copy"
            >
              <Link2 size={18} />
            </button>
          </div>
        </div>

        <div className="prose prose-invert mt-8 max-w-none prose-headings:font-medium prose-h2:text-xl prose-p:text-white/75 prose-p:leading-relaxed prose-img:rounded-2xl" dangerouslySetInnerHTML={{ __html: post.body }} />
      </article>
    </div>
  );
}
